import { isApolloError, gql } from '@apollo/client';
import { FlowSpec } from '@digibee/flow';

import clientApollo from './clientApollo';

import { Nullish } from '~/common/types/Nullish';
import type { TestPipelineMetadataInput } from '~/scenes/Build/helpers/getTestPipelineMetadataVars';

export type LogLevel = 'INFO' | 'WARN' | 'ERROR';

export type ExecutionMessage = {
  id: string;
  body: string; // a stringified JSON
  elapsed: number;
  processorStepName: string;
  processorType: string;
  connectorId: string;
  connectorName: string;
  timestampStart: number;
  timestampEnd: number;
  indexes: Record<string, number>;
};

export type ExecutionLog = {
  id: string;
  logLevel: LogLevel;
  logMessage: string;
  pipelineName: string;
  timestamp: string;
};

type ConvertJson = {
  message: string | undefined;
  status: number;
  data: unknown;
  'X-Digibee-Debug': string;
  'X-Digibee-Key': string;
};

type TestExecuteResult = {
  digibeeKey: string;
  data: string;
};

const controllers: AbortController[] = [];

const convertJson = (stringJson: string | undefined): ConvertJson => {
  if (!stringJson) return {} as ConvertJson;
  return JSON.parse(stringJson);
};

// execute

export type ExecuteParams = {
  realm: string;
  flowSpec: FlowSpec;
  payload: string;
  trackingId: string;
  parameterizedReplica: Nullish<string>;
  replicaInstanceName: Nullish<string>;
  scopes?: string[];
  metadata?: TestPipelineMetadataInput;
};

export type ExecuteMultipleTestCasesParams = {
  realm: string;
  trackingId: string;
  testCases: Array<{
    id: string;
    payload: string;
    flowSpec: FlowSpec;
    expectedResult: string;
    inSpec: unknown;
    outSpec: unknown;
  }>;
  inSpec: unknown;
  outSpec: unknown;
  environmentParameters: Array<{
    parameter: string;
    value: unknown;
    updatedAt: number;
    privateForUpdate: boolean;
    compound: boolean;
  }>;
  realmName: string;
};

type ExecuteResult = {
  testPipelineV2: string; // a stringified JSON
};

type ExecuteMultipleTestCasesResult = {
  executeMultipleTestCases: string; // a stringified JSON
};

const execute = async (params: ExecuteParams): Promise<TestExecuteResult> => {
  try {
    const controller = new window.AbortController();

    controllers.push(controller);

    const response = (await clientApollo.mutate({
      mutation: gql`
        mutation testPipelineV2(
          $realm: String!
          $flowSpec: JSON!
          $payload: String!
          $trackingId: String!
          $replicaInstanceName: String
          $parameterizedReplica: String
          $scopes: [String!]
          $metadata: TestPipelineMetadataInput
        ) {
          testPipelineV2(
            realm: $realm
            flowSpec: $flowSpec
            payload: $payload
            trackingId: $trackingId
            replicaInstanceName: $replicaInstanceName
            parameterizedReplica: $parameterizedReplica
            scopes: $scopes
            metadata: $metadata
          )
        }
      `,
      variables: {
        ...params
      },
      context: { fetchOptions: { signal: controller.signal } }
    })) as { data?: ExecuteResult };

    const { data, message, status, ...header } = convertJson(
      response?.data?.testPipelineV2
    );

    if ([400, 409, 500].includes(status)) {
      if (typeof message === 'string') throw new Error(message);

      throw new Error('Problem sending to test');
    }

    if (
      [502, 504].includes(status) ||
      (data as Record<string, unknown>)?.message === 'Timeout'
    ) {
      throw new Error('timeout');
    }

    if (!data && message) {
      throw new Error(message);
    }

    return {
      digibeeKey: header['X-Digibee-Key'],
      data: data as string
    };
  } catch (e) {
    if (e instanceof Error && isApolloError(e)) throw Error(e.message);

    throw e;
  }
};

const executeTestCasesBatch = async (
  params: ExecuteMultipleTestCasesParams
): Promise<TestExecuteResult> => {
  try {
    const controller = new window.AbortController();

    controllers.push(controller);

    const response = (await clientApollo.mutate({
      mutation: gql`
        mutation executeMultipleTestCases(
          $realm: String!
          $trackingId: String!
          $testCases: [TestCaseInput!]!
          $inSpec: JSON!
          $outSpec: JSON!
          $environmentParameters: [EnvironmentParameterInput!]!
          $realmName: String!
        ) {
          executeMultipleTestCases(
            realm: $realm
            trackingId: $trackingId
            testCases: $testCases
            inSpec: $inSpec
            outSpec: $outSpec
            environmentParameters: $environmentParameters
            realmName: $realmName
          )
        }
      `,
      variables: {
        ...params
      },
      context: { fetchOptions: { signal: controller.signal } }
    })) as { data?: ExecuteMultipleTestCasesResult };

    const { data, message, status, ...header } = convertJson(
      response?.data?.executeMultipleTestCases
    );

    if ([400, 409, 500].includes(status)) {
      if (typeof message === 'string') throw new Error(message);

      throw new Error('Problem sending to test');
    }

    if (
      [502, 504].includes(status) ||
      (data as Record<string, unknown>)?.message === 'Timeout'
    ) {
      throw new Error('timeout');
    }

    if (!data && message) {
      throw new Error(message);
    }

    return {
      digibeeKey: header['X-Digibee-Key'],
      data: data as string
    };
  } catch (e) {
    if (e instanceof Error && isApolloError(e)) throw Error(e.message);

    throw e;
  }
};

// get messages

type GetMessagesParams = {
  realm: string;
  key: string;
  environment: string;
  limit?: number;
};

type GetMessagesResult = {
  messagesByKey: ExecutionMessage[];
};

const getMessages = async (params: GetMessagesParams) => {
  try {
    const response = (await clientApollo.query({
      query: gql`
        query messagesByKey(
          $realm: String!
          $key: String
          $environment: String
        ) {
          messagesByKey(realm: $realm, key: $key, environment: $environment) {
            id
            body
            processorStepName
            processorType
            elapsed
            connectorId
            connectorName
            timestampStart
            timestampEnd
            indexes
          }
        }
      `,
      variables: {
        ...params
      }
    })) as { data: GetMessagesResult };
    return response.data;
  } catch (e) {
    throw new Error(e as string);
  }
};

// get logs

type GetLogsParams = {
  realm: string;
  key: string;
  env?: string;
};

type GetLogsResult = {
  logs: ExecutionLog[];
};

const getLogs = async (params: GetLogsParams) => {
  try {
    const response = (await clientApollo.query({
      query: gql`
        query logs(
          $realm: String!
          $key: String!
          $time: String
          $env: String
          $logIdAfter: String
        ) {
          logs(
            realm: $realm
            key: $key
            time: $time
            env: $env
            logIdAfter: $logIdAfter
          ) {
            id
            pipelineName
            logMessage
            timestamp
            logLevel
          }
        }
      `,
      variables: {
        ...params
      }
    })) as { data: GetLogsResult };
    return response.data;
  } catch (e) {
    throw new Error(e as string);
  }
};

const test = {
  execute,
  executeTestCasesBatch,
  getMessages,
  getLogs,
  cancel: (): void => {
    controllers.map(controller => controller.abort());
  }
};

export default test;
